import { useState, useEffect } from "react";
import { User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft } from "lucide-react";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import pokerChip from "@/assets/poker-chip.png";

interface AuthPageProps {
  onBack: () => void;
  onAuthSuccess: (user: User) => void;
}

export const AuthPage = ({ onBack, onAuthSuccess }: AuthPageProps) => {
  const [authMode, setAuthMode] = useState<"signin" | "signup" | "magic-link" | "forgot-password">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [linkSent, setLinkSent] = useState(false);
  const [canResendLink, setCanResendLink] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [isInviteFlow, setIsInviteFlow] = useState(false);
  const { toast } = useToast();

  // Check for invitation flow on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const isInvite = urlParams.has('invite');
    
    if (isInvite) {
      setIsInviteFlow(true);
      setAuthMode("signup");
      
      // Extract email from URL hash if available (from Supabase invitation token)
      const hash = window.location.hash;
      if (hash) {
        const params = new URLSearchParams(hash.substring(1));
        const tokenEmail = params.get('email');
        if (tokenEmail) {
          setEmail(tokenEmail);
        }
      }
    }
  }, []);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user) {
        onAuthSuccess(session.user);
      }
    });

    return () => subscription.unsubscribe();
  }, [onAuthSuccess]);

  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            setCanResendLink(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleEmailPasswordAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (authMode === "signup") {
        if (password !== confirmPassword) {
          throw new Error("Passwords do not match");
        }
        
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/`,
          },
        });
        
        if (error) throw error;
        
        toast({
          title: "Check your email",
          description: "We've sent you a confirmation link to complete your registration.",
        });
      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        
        if (error) throw error;
        
        toast({
          title: "Welcome back!",
          description: "You have successfully signed in.",
        });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleMagicLinkSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
        },
      });
      
      if (error) throw error;
      
      setLinkSent(true);
      setCanResendLink(false);
      setResendTimer(60);
      
      toast({
        title: "Magic link sent",
        description: "Check your email for the sign-in link.",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      setLinkSent(true);
      setCanResendLink(false);
      setResendTimer(60);
      
      toast({
        title: "Reset link sent",
        description: "Check your email for the password reset link.",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to send reset link",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResendLink = async () => {
    if (!canResendLink) return;
    
    setLoading(true);
    try {
      if (authMode === "magic-link") {
        const { error } = await supabase.auth.signInWithOtp({
          email,
          options: {
            emailRedirectTo: `${window.location.origin}/`,
          },
        });
        if (error) throw error;
      } else if (authMode === "forgot-password") {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/reset-password`,
        });
        if (error) throw error;
      }
      
      setCanResendLink(false);
      setResendTimer(60);
      
      toast({
        title: "Link resent",
        description: "Check your email for the new link.",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to resend link",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };


  const getCardTitle = () => {
    if (isInviteFlow && authMode === "signup") {
      return "Complete Your Invitation";
    }
    switch (authMode) {
      case "signup": return "Create Account";
      case "magic-link": return "Sign In with Magic Link";
      case "forgot-password": return "Forgot Password";
      default: return "Welcome Back";
    }
  };

  const getCardDescription = () => {
    if (isInviteFlow && authMode === "signup") {
      return "Complete your account setup to join your poker group";
    }
    switch (authMode) {
      case "signup": return "Start organizing better poker games today";
      case "magic-link": return linkSent ? "Check your email for the sign-in link" : "We'll send you a magic link to sign in";
      case "forgot-password": return linkSent ? "Check your email for the password reset link" : "Enter your email to receive a reset link";
      default: return "Access your poker groups and games";
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Button variant="ghost" onClick={onBack} className="mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
          <div className="flex items-center justify-center gap-3 mb-4">
            <img src={pokerChip} alt="SplitPot" className="w-10 h-10" />
            <h1 className="text-3xl font-bold text-foreground">SplitPot</h1>
          </div>
          <p className="text-muted-foreground">
            {getCardDescription()}
          </p>
        </div>

        <Card className="border-border">
          <CardHeader>
            <CardTitle>{getCardTitle()}</CardTitle>
          </CardHeader>
          <CardContent>
            {authMode === "magic-link" && (
              <form onSubmit={handleMagicLinkSignIn} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={linkSent}
                    required
                  />
                </div>
                <Button type="submit" className="w-full" disabled={loading || linkSent}>
                  {loading ? "Sending..." : (linkSent ? "Link sent - Check your email" : "Send Magic Link")}
                </Button>
                {linkSent && (
                  <div className="text-center">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleResendLink}
                      disabled={!canResendLink || loading}
                    >
                      {canResendLink ? "Resend link" : `Resend in ${resendTimer}s`}
                    </Button>
                  </div>
                )}
              </form>
            )}

            {authMode === "forgot-password" && (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={linkSent}
                    required
                  />
                </div>
                <Button type="submit" className="w-full" disabled={loading || linkSent}>
                  {loading ? "Sending..." : (linkSent ? "Reset link sent - Check your email" : "Send Reset Link")}
                </Button>
                {linkSent && (
                  <div className="text-center">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleResendLink}
                      disabled={!canResendLink || loading}
                    >
                      {canResendLink ? "Resend link" : `Resend in ${resendTimer}s`}
                    </Button>
                  </div>
                )}
              </form>
            )}


            {(authMode === "signin" || authMode === "signup") && (
              <form onSubmit={handleEmailPasswordAuth} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isInviteFlow}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
                {authMode === "signup" && (
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm Password</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="Confirm your password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                    />
                  </div>
                )}
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? "Please wait..." : (authMode === "signup" ? "Create Account" : "Sign In")}
                </Button>
              </form>
            )}
            
            <div className="mt-6 space-y-3">
              {authMode === "signin" && (
                <>
                  <div className="text-center">
                    <Button
                      type="button"
                      variant="link"
                      onClick={() => setAuthMode("magic-link")}
                      className="p-0 h-auto text-sm"
                    >
                      Sign in via email magic link
                    </Button>
                  </div>
                  <div className="text-center">
                    <Button
                      type="button"
                      variant="link"
                      onClick={() => setAuthMode("forgot-password")}
                      className="p-0 h-auto text-sm"
                    >
                      Forgot your password?
                    </Button>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">
                      Don't have an account?{" "}
                      <Button
                        type="button"
                        variant="link"
                        onClick={() => setAuthMode("signup")}
                        className="p-0 h-auto"
                      >
                        Sign up
                      </Button>
                    </p>
                  </div>
                </>
              )}
              
              {authMode === "signup" && (
                <div className="text-center">
                  <p className="text-sm text-muted-foreground">
                    Already have an account?{" "}
                    <Button
                      type="button"
                      variant="link"
                      onClick={() => setAuthMode("signin")}
                      className="p-0 h-auto"
                    >
                      Sign in
                    </Button>
                  </p>
                </div>
              )}
              
              {(authMode === "magic-link" || authMode === "forgot-password") && (
                <div className="text-center">
                  <Button
                    type="button"
                    variant="link"
                    onClick={() => {
                      setAuthMode("signin");
                      setLinkSent(false);
                      setCanResendLink(false);
                      setResendTimer(0);
                    }}
                    className="p-0 h-auto text-sm"
                  >
                    Back to sign in
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};